import { getToken } from "firebase/app-check";
import { httpsCallable } from "firebase/functions";
import { appCheck, functions } from "./firebaseSetup";
import type {
  ConfirmReviewEditResponse,
  ConfirmReviewResponse,
  GetDisputeForEvidenceResponse,
  GetReviewForEditResponse,
  InvitationResponse,
  ResendResponse,
  RevokeReviewResponse,
  SubmitDisputeEvidencePayload,
  SubmitDisputeEvidenceResponse,
  SubmitReviewPayload,
  SubmitReviewResponse,
  UpdateReviewPayload,
  UpdateReviewResponse,
} from "./types";

const getReviewInvitationFn = httpsCallable<
  { linkToken: string; appCheckToken: string },
  InvitationResponse
>(functions, "getReviewInvitation");

const submitReviewFn = httpsCallable<
  SubmitReviewPayload & { appCheckToken: string },
  SubmitReviewResponse
>(functions, "submitReview");

const confirmReviewFn = httpsCallable<
  { token: string; appCheckToken: string },
  ConfirmReviewResponse
>(functions, "confirmReview");

const resendReviewConfirmationFn = httpsCallable<
  { pendingId: string; appCheckToken: string },
  ResendResponse
>(functions, "resendReviewConfirmation");

const getReviewForEditFn = httpsCallable<
  { editToken: string; appCheckToken: string },
  GetReviewForEditResponse
>(functions, "getReviewForEdit");

const updateReviewFn = httpsCallable<
  UpdateReviewPayload & { appCheckToken: string },
  UpdateReviewResponse
>(functions, "updateReview");

const confirmReviewEditFn = httpsCallable<
  { token: string; appCheckToken: string },
  ConfirmReviewEditResponse
>(functions, "confirmReviewEdit");

const resendReviewEditConfirmationFn = httpsCallable<
  { pendingId: string; appCheckToken: string },
  ResendResponse
>(functions, "resendReviewEditConfirmation");

const revokeReviewFn = httpsCallable<
  { revokeToken: string; appCheckToken: string },
  RevokeReviewResponse
>(functions, "revokeReview");

const getDisputeForEvidenceFn = httpsCallable<
  { evidenceToken: string; appCheckToken: string },
  GetDisputeForEvidenceResponse
>(functions, "getDisputeForEvidence");

const submitDisputeEvidenceFn = httpsCallable<
  SubmitDisputeEvidencePayload & { appCheckToken: string },
  SubmitDisputeEvidenceResponse
>(functions, "submitDisputeEvidence");

async function getAppCheckToken(): Promise<string> {
  if (!appCheck) return "";
  try {
    const result = await getToken(appCheck, false);
    return result.token;
  } catch {
    return "";
  }
}

export async function fetchInvitation(
  linkToken: string
): Promise<InvitationResponse> {
  const appCheckToken = await getAppCheckToken();
  const res = await getReviewInvitationFn({ linkToken, appCheckToken });
  return res.data;
}

export async function submitReview(
  payload: SubmitReviewPayload
): Promise<SubmitReviewResponse> {
  const appCheckToken = await getAppCheckToken();
  const res = await submitReviewFn({ ...payload, appCheckToken });
  return res.data;
}

export async function confirmReview(
  token: string
): Promise<ConfirmReviewResponse> {
  const appCheckToken = await getAppCheckToken();
  const res = await confirmReviewFn({ token, appCheckToken });
  return res.data;
}

export async function resendConfirmation(
  pendingId: string
): Promise<ResendResponse> {
  const appCheckToken = await getAppCheckToken();
  const res = await resendReviewConfirmationFn({ pendingId, appCheckToken });
  return res.data;
}

export async function fetchReviewForEdit(
  editToken: string
): Promise<GetReviewForEditResponse> {
  const appCheckToken = await getAppCheckToken();
  const res = await getReviewForEditFn({ editToken, appCheckToken });
  return res.data;
}

export async function updateReview(
  payload: UpdateReviewPayload
): Promise<UpdateReviewResponse> {
  const appCheckToken = await getAppCheckToken();
  const res = await updateReviewFn({ ...payload, appCheckToken });
  return res.data;
}

export async function confirmReviewEdit(
  token: string
): Promise<ConfirmReviewEditResponse> {
  const appCheckToken = await getAppCheckToken();
  const res = await confirmReviewEditFn({ token, appCheckToken });
  return res.data;
}

export async function resendEditConfirmation(
  pendingId: string
): Promise<ResendResponse> {
  const appCheckToken = await getAppCheckToken();
  const res = await resendReviewEditConfirmationFn({ pendingId, appCheckToken });
  return res.data;
}

export async function revokeReview(
  revokeToken: string
): Promise<RevokeReviewResponse> {
  const appCheckToken = await getAppCheckToken();
  const res = await revokeReviewFn({ revokeToken, appCheckToken });
  return res.data;
}

export async function fetchDisputeForEvidence(
  evidenceToken: string
): Promise<GetDisputeForEvidenceResponse> {
  const appCheckToken = await getAppCheckToken();
  const res = await getDisputeForEvidenceFn({ evidenceToken, appCheckToken });
  return res.data;
}

export async function submitDisputeEvidence(
  payload: SubmitDisputeEvidencePayload
): Promise<SubmitDisputeEvidenceResponse> {
  const appCheckToken = await getAppCheckToken();
  const res = await submitDisputeEvidenceFn({ ...payload, appCheckToken });
  return res.data;
}
